import { useCallback, useEffect, useRef } from "react";

export const PAYMENT_IFRAME_EMBED_TIMEOUT_MS = 25_000;

function isFrameSrcCspViolationForUrl(
  event: SecurityPolicyViolationEvent,
  ecommerceLink: string
): boolean {
  const directive =
    event.effectiveDirective || event.violatedDirective || "";
  if (
    !directive.includes("frame-src") &&
    !directive.includes("child-src")
  ) {
    return false;
  }

  try {
    const targetOrigin = new URL(ecommerceLink).origin;
    const blocked = event.blockedURI;
    if (!blocked || blocked === "about:blank") {
      return false;
    }
    return new URL(blocked).origin === targetOrigin;
  } catch {
    return false;
  }
}

type UsePaymentIframeEmbedGuardOptions = {
  ecommerceLink: string | null;
  iframeLoaded: boolean;
  enabled?: boolean;
  timeoutMs?: number;
  onEmbedFailed: () => void;
};

// CSP frame-src blocks and some network failures never fire iframe onError/onLoad.
export function usePaymentIframeEmbedGuard({
  ecommerceLink,
  iframeLoaded,
  enabled = true,
  timeoutMs = PAYMENT_IFRAME_EMBED_TIMEOUT_MS,
  onEmbedFailed
}: UsePaymentIframeEmbedGuardOptions): void {
  const embedFailedRef = useRef(false);
  const onEmbedFailedRef = useRef(onEmbedFailed);
  onEmbedFailedRef.current = onEmbedFailed;

  const fireEmbedFailed = useCallback(() => {
    if (embedFailedRef.current || !enabled) return;
    embedFailedRef.current = true;
    onEmbedFailedRef.current();
  }, [enabled]);

  useEffect(() => {
    embedFailedRef.current = false;
  }, [ecommerceLink]);

  useEffect(() => {
    if (!enabled || !ecommerceLink || iframeLoaded) return;

    const onViolation = (event: SecurityPolicyViolationEvent) => {
      if (isFrameSrcCspViolationForUrl(event, ecommerceLink)) {
        fireEmbedFailed();
      }
    };

    document.addEventListener("securitypolicyviolation", onViolation);
    return () => {
      document.removeEventListener("securitypolicyviolation", onViolation);
    };
  }, [ecommerceLink, iframeLoaded, enabled, fireEmbedFailed]);

  useEffect(() => {
    if (!enabled || !ecommerceLink || iframeLoaded) return;

    const timer = globalThis.setTimeout(() => {
      fireEmbedFailed();
    }, timeoutMs);

    return () => globalThis.clearTimeout(timer);
  }, [ecommerceLink, iframeLoaded, enabled, timeoutMs, fireEmbedFailed]);
}
